import { DeleteOutline } from "@mui/icons-material";
import { Button, Stack } from "@mui/material";
import { FC, useState } from "react";
import { LabelValue } from "../../../../components/LabelValue";
import { useLocalization } from "../../../../hooks/useLocalization";
import { LicenseWithExperienceName } from "../../hooks/useLicenseById";
import { useIsWhitelistEnabled } from "../../hooks/useWhitelistFeatureFlag";
import { DeleteLicenseDialog } from "../DeleteLicenseDialog";
import { EditExpirationDateDialog } from "../EditExpirationDateDialog";
import { EditSeatsDialog } from "../EditSeatsDialog";
import { ExpirationDateView } from "../ExpirationDateView";
import { RenewalView } from "../RenewalView";
import { SeatsView } from "../SeatsView";
import { WhitelistStatusView } from "../WhitelistStatusView";

interface OverviewTabProps {
  license: LicenseWithExperienceName | undefined;
}

export const OverviewTab: FC<OverviewTabProps> = ({ license }) => {
  const { t } = useLocalization();
  const isWhitelistEnabled = useIsWhitelistEnabled();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditSeatsDialogOpen, setIsEditSeatsDialogOpen] = useState(false);
  const [isEditExpirationDateDialogOpen, setIsEditExpirationDateDialogOpen] =
    useState(false);

  const onDeleteLicense = () => {
    setIsDeleteDialogOpen(true);
  };

  const onCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
  };

  const onEditSeatsClick = () => {
    setIsEditSeatsDialogOpen(true);
  };

  const onEditExpirationDateClick = () => {
    setIsEditExpirationDateDialogOpen(true);
  };

  return (
    <>
      <Stack spacing={{ xs: "24px", md: "8px" }}>
        <LabelValue
          containerSx={{ height: "48px" }}
          label={t("licenses.company")}
          value={license?.assignedOrganizationName}
        />
        <LabelValue
          containerSx={{ height: "48px" }}
          label={t("licenses.occupiedSeats")}
          value={license?.allocatedSeats}
        />
        <LabelValue
          containerSx={{ height: "48px" }}
          label={t("licenses.seats")}
        >
          <SeatsView onClick={onEditSeatsClick} seats={license?.seats} />
        </LabelValue>
        <LabelValue
          containerSx={{ height: "48px" }}
          label={t("licenses.expirationDate")}
        >
          <ExpirationDateView
            expirationDate={license?.expiresAt}
            onClick={onEditExpirationDateClick}
          />
        </LabelValue>
        {isWhitelistEnabled && (
          <LabelValue
            containerSx={{ height: "48px" }}
            label={t("licenses.whitelistLabel")}
          >
            <WhitelistStatusView
              hasWhitelist={license?.hasWhitelist ?? false}
            />
          </LabelValue>
        )}
        {/* Only show renewal section for licenses created with a renewal period */}
        {license?.seats && license.renewalPeriodDays > 0 && (
          <LabelValue
            containerSx={{ height: "62px" }}
            label={t("licenses.renewalEvery")}
          >
            <RenewalView
              renewalDate={license.renewalDate}
              renewalPeriodDays={license.renewalPeriodDays}
              seats={license.seats}
            />
          </LabelValue>
        )}
        <LabelValue
          containerSx={{ pt: "48px", height: "88px" }}
          label={t("licenses.deleteLicense")}
        >
          <Button onClick={onDeleteLicense} variant="secondary">
            <DeleteOutline sx={{ fontSize: 24, mr: 1 }} />
            {t("licenses.delete")}
          </Button>
        </LabelValue>
      </Stack>

      <DeleteLicenseDialog
        companyName={license?.assignedOrganizationName}
        deletedLicenseName={`${license?.assignedOrganizationName} - ${license?.experienceName}`}
        licenseId={license?.licenseId}
        onClose={onCloseDeleteDialog}
        open={isDeleteDialogOpen}
      />
      {license && (
        <>
          <EditSeatsDialog
            license={license}
            onClose={() => setIsEditSeatsDialogOpen(false)}
            open={isEditSeatsDialogOpen}
          />
          <EditExpirationDateDialog
            license={license}
            onClose={() => setIsEditExpirationDateDialogOpen(false)}
            open={isEditExpirationDateDialogOpen}
          />
        </>
      )}
    </>
  );
};
